/**
 * This module holds all of the implementations for keeping GraphQL data in sync with Flux and Redux.
 */
import {mapObject, objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {
  BOX_SCHEMAS_FETCH_COMPLETED,
  BOX_SCHEMAS_UPDATED_FROM_GRAPHQL,
  CONTAINER_SCHEMAS_FETCH_COMPLETED,
  CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL,
  FILE_BROWSER_SOURCES_FETCHED,
  LOCATION_SCHEMAS_FETCH_COMPLETED,
  LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL,
  PLATE_SCHEMAS_FETCH_COMPLETED,
  PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL,
  SOURCES_UPDATED_FROM_GRAPHQL,
} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import AliasesForBioentitySyncer from 'graphql-client/sync/syncers/AliasesForBioentitySyncer';
import AppDispatcher from 'lib/Dispatcher';
import {ManagedModelType, ManagedModelTypes} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import {ManagedModelSelector} from 'models/reduceManagedModels';
import NavStore from 'nav/NavStore';
import {entriesFetchCompleted} from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {requestsFetchCompleted} from 'requests/RequestReduxActions';
import {dispatchAssaySchemasListed, dispatchAssaySchemasUpdatedFromGQL} from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {dispatchStorablesLoaded, dispatchStorableSummariesLoaded} from 'samples/StorableActions';
import {gridSummariesFromStoreToValidMapOfType} from 'samples/storables/graphql/gridsFromStoreToValidMapOfType';
import StorableStore from 'samples/StorableStore';

import SyncerFactory from './SyncerFactory';
import RequestSampleGroupsRelationshipSyncer from './syncers/RequestSampleGroupsRelationshipSyncer';
import RequestTasksRelationshipSyncer from './syncers/RequestTasksRelationshipSyncer';
import SampleGroupSchemaSyncer from './syncers/SampleGroupSchemaSyncer';
import {SyncDeclarationInterface, SyncInterface, TFragmentName} from './SyncInterface';

function getAllManagedModelsByKey<T extends keyof ManagedModelTypes>(
  modelType: T
): {[key: string]: ManagedModelTypes[T]} {
  return new ManagedModelSelector(getStore().getState()).getAllModelsByKey(modelType);
}

const ACCESSIBLE_OR_INACCESSIBLE_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  EntrySummaryOrInaccessibleGraphQL: {
    readFromStore: () => {
      const entrySummariesById = getStore().getState().models.entrySummariesById ?? {};
      const invalidIds = getStore().getState().models.inaccessibleEntryIds;

      // We can't tell the value of canRequestAccess from the id alone, so must default to true.
      const inaccessibleFilesById = objectFromEntries(
        invalidIds.map((invalidId) => [invalidId, {invalidId, canRequestAccess: true}])
      );

      return {
        current: {...entrySummariesById, ...inaccessibleFilesById},
        equalityRef: {entrySummariesById, invalidIds},
      };
    },
    dispatchAccessibleOrInaccessibleUpdates: (restObjsByID, inaccessibleIds) => {
      getStore().dispatch(entriesFetchCompleted(restObjsByID, inaccessibleIds));
    },
  },
};

const ROOT_QUERY_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  NavStoreRegistryGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (NavStore.sources) {
        AppDispatcher.dispatch({
          actionType: SOURCES_UPDATED_FROM_GRAPHQL,
          sources: Object.values(restObjsByID),
        });
      }
    },
  },
};

const ROOT_COLLECTION_PAIR_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  AllAssaySchemasGraphQL: {
    stores: [AssayStore],
    readCollectionFromStore: () => ({
      current: AssayStore.assaySchemasById
        ? {
            requestSchemas: AssayStore.getRequestSchemas() ?? [],
            resultSchemas: AssayStore.getResultSchemas() ?? [],
            runSchemas: AssayStore.getRunSchemas() ?? [],
          }
        : null,
      equalityRef: AssayStore.assaySchemasById,
    }),
    dispatchCollectionUpdates: (restResult) => {
      dispatchAssaySchemasListed([
        ...restResult.requestSchemas,
        ...restResult.resultSchemas,
        ...restResult.runSchemas,
      ]);
    },
  },
  AssaySchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (AssayStore.assaySchemasById) {
        dispatchAssaySchemasUpdatedFromGQL(Object.values(restObjsByID));
      }
    },
  },
  AllBoxSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.boxSchemas ? {boxSchemas: SampleSchemaStore.boxSchemas} : null,
      equalityRef: SampleSchemaStore.boxSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: BOX_SCHEMAS_FETCH_COMPLETED,
        boxSchemas: restResult.boxSchemas,
      });
    },
  },
  BoxSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.boxSchemas) {
        AppDispatcher.dispatch({
          actionType: BOX_SCHEMAS_UPDATED_FROM_GRAPHQL,
          boxSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllContainerSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.containerSchemas
        ? {containerSchemas: SampleSchemaStore.containerSchemas}
        : null,
      equalityRef: SampleSchemaStore.containerSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: CONTAINER_SCHEMAS_FETCH_COMPLETED,
        containerSchemas: restResult.containerSchemas,
      });
    },
  },
  ContainerSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.containerSchemas) {
        AppDispatcher.dispatch({
          actionType: CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL,
          containerSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllLocationSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.locationSchemas
        ? {locationSchemas: SampleSchemaStore.locationSchemas}
        : null,
      equalityRef: SampleSchemaStore.locationSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: LOCATION_SCHEMAS_FETCH_COMPLETED,
        locationSchemas: restResult.locationSchemas,
      });
    },
  },
  LocationSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.locationSchemas) {
        AppDispatcher.dispatch({
          actionType: LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL,
          locationSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllPlateSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.plateSchemas ? {plateSchemas: SampleSchemaStore.plateSchemas} : null,
      equalityRef: SampleSchemaStore.plateSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: PLATE_SCHEMAS_FETCH_COMPLETED,
        plateSchemas: restResult.plateSchemas,
      });
    },
  },
  PlateSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.plateSchemas) {
        AppDispatcher.dispatch({
          actionType: PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL,
          plateSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  RequestSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (AssayStore.assaySchemasById) {
        dispatchAssaySchemasUpdatedFromGQL(Object.values(restObjsByID));
      }
    },
  },
  AllSourcesGraphQL: {
    stores: [NavStore],
    readCollectionFromStore: () => ({
      current: NavStore.sources ? {sources: NavStore.sources} : null,
      equalityRef: NavStore.sources,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: FILE_BROWSER_SOURCES_FETCHED,
        sources: restResult.sources,
      });
    },
  },
  NavStoreSourceGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (NavStore.sources) {
        AppDispatcher.dispatch({
          actionType: SOURCES_UPDATED_FROM_GRAPHQL,
          sources: Object.values(restObjsByID),
        });
      }
    },
  },
};

const MODEL_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  BoxSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridSummariesFromStoreToValidMapOfType(StorableStore.gridSummaries, ModelTypeConstants.BOX),
      equalityRef: StorableStore.gridSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  ContainerGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.containers,
      equalityRef: StorableStore.containers,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorablesLoaded(Object.values(restObjsByID));
    },
  },
  ContainerSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.containerSummaries,
      equalityRef: StorableStore.containerSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  FolderWithPermissionsGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.FOLDER_BY_ID),
      equalityRef: getStore().getState().models.managed[ManagedModelType.FOLDER_BY_ID],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.FOLDER_BY_ID, restObjsByID));
    },
  },
  LocationSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.locationSummaries,
      equalityRef: StorableStore.locationSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  PlateSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridSummariesFromStoreToValidMapOfType(StorableStore.gridSummaries, ModelTypeConstants.PLATE),
      equalityRef: StorableStore.gridSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  ProtocolSummaryGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.PROTOCOL_SUMMARY),
      equalityRef: getStore().getState().models.managed[ManagedModelType.PROTOCOL_SUMMARY],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.PROTOCOL_SUMMARY, restObjsByID));
    },
  },
  RequestGraphQL: {
    readFromStore: () => {
      const requestsById = getStore().getState().models.requests.inputs.requestsById;
      return {
        current: requestsById,
        equalityRef: requestsById,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(requestsFetchCompleted(Object.values(restObjsByID)));
    },
  },
};

const BASE_GENERATED_SYNC_DEFINITIONS = SyncerFactory.getFactorySyncDefinitions({
  ...ACCESSIBLE_OR_INACCESSIBLE_SYNCER_DEFINITIONS,
  ...ROOT_QUERY_SYNCER_DEFINITIONS,
  ...ROOT_COLLECTION_PAIR_SYNCER_DEFINITIONS,
  ...MODEL_SYNCER_DEFINITIONS,
});

// Re-route syncer activity of a "child" fragment type to a "parent" syncer capable of handling a union of
// fragment types.
const PROXIED_SYNCERS: {[key in TFragmentName]?: TFragmentName} = {
  EntrySummaryGraphQL: 'EntrySummaryOrInaccessibleGraphQL',
};

function getProxiedSyncDefinitions(): SyncInterface {
  return mapObject(PROXIED_SYNCERS, (value: TFragmentName) => {
    assert(
      BASE_GENERATED_SYNC_DEFINITIONS[value] != null,
      `Tried to proxy to a syncer that does not exist. Looked for ${value}.`
    );

    return BASE_GENERATED_SYNC_DEFINITIONS[value];
  });
}

const SYNC_DEFINITIONS: SyncInterface = {
  ...BASE_GENERATED_SYNC_DEFINITIONS,
  ...getProxiedSyncDefinitions(),
  AliasesForBioentityGraphQL: new AliasesForBioentitySyncer(),
  GraphQLRequestSampleGroupsRelationship: new RequestSampleGroupsRelationshipSyncer(),
  GraphQLRequestTasksRelationship: new RequestTasksRelationshipSyncer(),
  SampleGroupSchemaGraphQL: new SampleGroupSchemaSyncer(),
};

export default SYNC_DEFINITIONS;
